import axios from 'axios';
import qs from 'query-string';
import { getRequestIdentityHeader } from '@meitu/xdesign-identity';
import { getConfig } from '@/configProvider';
import { getApiHost, getSubApiHost } from '@/utils/env';
import { getCommonParams } from '@/utils/request';
import { getTokenKey } from '../utils';

/**
 * 接口 [查询静默扣美豆协议是否勾选]的请求数据
 * @docs: /purchase/silent_agreement_detail
 */
export interface SilentAgreementDetailParams {
    /**
     * 大账号client_id
     */
    client_id: number;
}

/**
 * 接口 [查询静默扣美豆协议是否勾选]的返回数据
 * @docs: /purchase/silent_agreement_detail
 */
export interface SilentAgreementDetailResponse {
    reqid: string;
    code: number;
    data: {
        /**
         * 该功能渠道下是否已经勾选静默扣美豆。1=是；2=否
         */
        status: number;
    };
}

/**
 * 接口 [更新静默扣美豆协议]的请求数据
 * @docs: /purchase/silent_agreement_change
 */
export interface SilentAgreementChangeParams {
    /**
     * 客户端client_id
     */
    client_id: number;
    /**
     * 更新协议勾选状态：1=勾选；2=取消勾选
     */
    status: string;
}

/**
 * 接口 [更新静默扣美豆协议]的返回数据
 * @docs: /purchase/silent_agreement_change
 */
export interface SilentAgreementChangeResponse {
    reqid: string;
    code: number;
    data: {
        /**
         * 该功能渠道下是否已经勾选静默扣美豆。1=是；2=否
         */
        status: number;
    };
}

/**
 * 接口 [美豆算价—AI商品图任务]的请求数据
 * @docs: /v1/purchase/price_calculate
 */
export interface PriceCalculateParams {
    /**
     * 功能key，AI商品图：ai_product
     */
    subfunc: string;
    /**
     * 大账号client_id
     */
    client_id: string;
    /**
     * AI商品图投递任务的完整参数，JSON字符串形式
     */
    task_param: string;
    /**
     * 地区，不填则默认cn
     */
    country_code?: string;
    /**
     * 任务数，默认1
     */
    num?: number;
    /**
     * 是否限免
     */
    need_free?: boolean;

    ignore_login?: boolean;
    /**
     * 教育角色
     */
    education_role?: string;
}

/**
 * 接口 [美豆算价—AI商品图任务]的返回数据
 * @docs: /v1/purchase/price_calculate
 */
export interface PriceCalculateResponse {
    reqid: string;
    code: number;
    data?: {
        /**
         * 付款类型：1=限免；2=美豆支付; 5=团队版美豆限额
         */
        type: number;
        /**
         * 功能限免的剩余次数
         */
        left_num: number;
        /**
         * 功能限免的额度
         */
        free_num: number;
        /**
         * 支付美豆标价（划线价），type=2且支持美豆支付时有值，单位个
         */
        price: number;
        /**
         * 原始单条价格，折扣展示时可作为划线价
         */
        original_unit_price?: number;
        /**
         * 折扣后单条价格，单位个
         */
        discount_unit_price?: number;
        /**
         * 折扣剩余次数
         */
        discount_left_num?: number;
        /**
         * 实际支付美豆价格，type=2且支持美豆支付时有值，单位个
         */
        payment_amount: number;
        /**
         * 剩余可用豆
         */
        available_amount: number;
        /**
         * 总剩余豆
         */
        total_amount: number;
        /**
         * 是否可以直接提交任务，当有足够限免或者足够美豆扣时，该值返回true
         */
        success: boolean;
        message: string;
        // 消耗提示
        consume_tip?: string;
        /**
         * 当命中策略但用户无权益时的端上引导策略
         */
        guide_policy?: number;
        use_v2?: boolean;
    };
}

export interface TransferDataParams {
    /**
     * Json字符串, 基础数据
     */
    base_data: string;
    /**
     * Json字符串, 大数据
     */
    big_data: string;
    /**
     * Json字符串, 业务数据
     */
    business_data: string;
}

export interface TransferDataResponse {
    error_code?: string;
    code?: number;
    message?: string;
    data?: {
        /**
         * 透传id，32位UUID
         */
        transfer_id: string;
    };
}

export interface AiServiceAnnouncementResponse {
    error_code?: string;
    code?: number;
    message?: string;
    data?: {
        message: string;
        title: string;
        type: string;
    };
}

/**
 * 接口 [每日登录领取美豆]的返回数据
 * @docs: /v1/coin/daily_presented
 */
export interface DailyPresentedResponse {
    reqid?: string;
    code?: number;
    data?: {
        /**
         * 美豆领取情况：0=领取失败；1=领取成功；2=已领取过
         */
        success?: number;
        /**
         * 弹窗标题
         */
        title?: string;
        /**
         * 弹窗副标题
         */
        sub_title?: string;
    };
}

export interface DayNumType {
    /**
     * 剩余有效天数
     */
    day: number;
    /**
     * 对应的美豆（美叶）数
     */
    num: number;
}
/**
 * 接口 [查询用户美豆明细]的返回数据
 * @docs: /v1/coin/coin_detail
 */
export interface CoinDetailResponse {
    reqid?: string;
    code?: number;
    data?: {
        /**
         * 总余额（美豆+美叶）
         */
        total_amount?: number;
        /**
         * 永久有效美豆数（美豆+永久美叶）
         */
        total_forever_amount?: number;
        /**
         * 美豆可用总量（付费永久）
         */
        meidou_available_amount?: number;
        /**
         * 美叶可用总量
         */
        meiye_available_amount?: number;
        /**
         * 永久美叶数量
         */
        meiye_forever_amount?: number;
        /**
         * 跟随会员美叶数量
         */
        meiye_follow_amount?: number;
        /**
         * 限期有效的美叶数量，排序按过期时间近到远排
         */
        day_num_list?: DayNumType[];
        /**
         * 积分冻结数量
         */
        meiye_freeze_amount?: number;
        /**
         * 会员美叶数量
         */
        meiye_member_amount?: number;
        /**
         * 跟随会员美叶当月到期数量
         */
        meiye_month_expire_amount?: number;
        /**
         * 其他来源获得的美叶数量
         */
        other_amount?: number;
    };
}

export interface AgentCoinDetailResponse {
    reqid?: string;
    code?: number;
    data?: {
        total_amount: number;
        today_expire_amount: number;
        other_amount: number;
    };
}

export interface AgentFuncCategoryItem {
    category?: string;
    unit: string;
    child_category: string;
    desc: string;
    coin_cost_num: number;

    /**
     * 高级模式最小美豆消耗
     */
    high_pattern_min_cost: number;

    /**
     * 高级模式最大美豆消耗
     */
    high_pattern_max_cost: number;
}

export interface AgentFuncCategory {
    category: string;
    category_list: AgentFuncCategoryItem[];
}
export interface AgentPriceResponse {
    code?: number;
    data?: AgentFuncCategory[];
}

/**
 * 接口 [查询静默扣美豆协议是否勾选↗](/purchase/silent_agreement_detail)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/133475
 */
export const silentAgreementDetail = async function (params: SilentAgreementDetailParams) {
    return await axios<SilentAgreementDetailResponse>(`${getApiHost()}/v1/purchase/silent_agreement_detail`, {
        method: 'GET',
        params: {
            ...getCommonParams(),
            ...params,
        },
        headers: {
            access_token: window.localStorage.getItem(getTokenKey()),
            ...getRequestIdentityHeader(),
        },
        withCredentials: true,
    });
};

/**
 * 接口 [更新静默扣美豆协议↗](/purchase/silent_agreement_change)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/133482
 */
export const silentAgreementChange = async function (params: SilentAgreementChangeParams, hideStringify?: boolean) {
    return await axios<SilentAgreementChangeResponse>(`${getApiHost()}/v1/purchase/silent_agreement_change`, {
        method: 'POST',
        data: !hideStringify ? qs.stringify({ ...getCommonParams(), ...params }) : { ...getCommonParams(), ...params },
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            access_token: window.localStorage.getItem(getTokenKey()),
            ...getRequestIdentityHeader(),
        },
        withCredentials: true,
        params: {
            client_id: getConfig().client_id,
            ts_random_id: getCommonParams().ts_random_id,
        },
    });
};

/**
 * 接口 [美豆算价—AI商品图任务↗](/v1/purchase/price_calculate)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/133493
 */
export const priceCalculate = async function (
    params: PriceCalculateParams,
    hideStringify?: boolean,
    // 新增 v2 策略兼容
    isFreeV2?: boolean,
    needFree?: boolean,
) {
    return await axios<PriceCalculateResponse>(`${getApiHost()}/v1/purchase/price_calculate`, {
        method: 'POST',
        data: !hideStringify
            ? qs.stringify({ ...getCommonParams(), ...params })
            : { ...getCommonParams(), ...params, ignore_login: true },
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            access_token: window.localStorage.getItem(getTokenKey()),
            ...getRequestIdentityHeader(),
        },
        withCredentials: true,
        params: {
            client_id: getConfig().client_id,
            ts_random_id: getCommonParams().ts_random_id,
            // 新增 v2 策略兼容
            ...(isFreeV2 ? { free_v2: true } : {}),
            ...(needFree ? { need_free: true } : {}),
        },
    });
};

/**
 * 接口 [生成透传数据ID(H5)↗](/h5/transaction/v2/transfer_data.json)
 * @docs: https://api-mock.meitu-int.com/project/1698/interface/api/104028
 */
export const createTransferId = async function (params: TransferDataParams, hideStringify?: boolean) {
    return await axios<TransferDataResponse>(`${getSubApiHost()}/h5/transaction/v2/transfer_data.json`, {
        method: 'POST',
        data: !hideStringify ? qs.stringify({ ...getCommonParams(), ...params }) : { ...getCommonParams(), ...params },
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        withCredentials: true,
        params: {
            client_id: getConfig().client_id,
            ts_random_id: getCommonParams().ts_random_id,
        },
    });
};

export const aiServiceAnnouncement = async function (params: any) {
    return await axios<AiServiceAnnouncementResponse>(`${getApiHost()}/common/ai_service_announcement`, {
        method: 'GET',
        params: {
            ...getCommonParams(),
            ...params,
        },
        headers: {
            access_token: window.localStorage.getItem(getTokenKey()),
            ...getRequestIdentityHeader(),
        },
        withCredentials: true,
    });
};

/**
 * 接口 [每日登录领取美豆↗](/v1/coin/daily_presented)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/136222
 */
export const dailyPresented = async function () {
    return await axios<DailyPresentedResponse>(`${getApiHost()}/v1/coin/daily_presented`, {
        method: 'POST',
        data: { ...getCommonParams() },
        headers: {
            access_token: window.localStorage.getItem(getTokenKey()),
            'Content-Type': 'application/x-www-form-urlencoded',
            ...getRequestIdentityHeader(),
        },
        withCredentials: true,
        params: {
            client_id: getConfig().client_id,
            ts_random_id: getCommonParams().ts_random_id,
        },
    });
};

/**
 * 美豆明细
 *
 * @see https://api-mock.meitu-int.com/project/2110/interface/api/136229
 */
export type MeiDouDetail = CoinDetailResponse['data'];

/**
 * 接口 [查询用户美豆明细↗](/v1/coin/coin_detail)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/136229
 */
export const coinDetail = async function () {
    return await axios<CoinDetailResponse>(`${getApiHost()}/v1/coin/coin_detail`, {
        headers: {
            access_token: window.localStorage.getItem(getTokenKey()),
            ...getRequestIdentityHeader(),
        },
        method: 'GET',
        params: { ...getCommonParams() },
    });
};

/**
 * 接口 [注册赠送-海外项目专用↗](/v1/coin/register_presented)
 * @docs: https://api-mock.meitu-int.com/project/2110/interface/api/145430
 */
export const registerPresented = async function () {
    return await axios<DailyPresentedResponse>(`${getApiHost()}/v1/coin/register_presented`, {
        method: 'POST',
        data: { ...getCommonParams() },
        headers: {
            access_token: window.localStorage.getItem(getTokenKey()),
            ...getRequestIdentityHeader(),
        },
        withCredentials: true,
        params: {
            client_id: getConfig().client_id,
            ts_random_id: getCommonParams().ts_random_id,
        },
    });
};

export const getAgentPrice = async function () {
    return await axios<AgentPriceResponse>(`${getApiHost()}/v1/agent/price`, {
        method: 'GET',
        params: { ...getCommonParams() },
    });
};
